import { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  getRandomMessage,
  getStreakMilestoneMessage,
  CONFETTI_COLORS,
} from "../utils/gamificationHelpers";

interface CelebrationProps {
  isVisible: boolean;
  xpEarned: number;
  currentStreak: number;
  streakIncremented: boolean;
  score: number;
  freezeUsed?: boolean;
  onDismiss: () => void;
}

function ConfettiParticle({ index }: { index: number }) {
  const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length];
  const left = Math.random() * 100;
  const delay = Math.random() * 0.8;
  const duration = 2 + Math.random() * 1.5;
  const size = 6 + Math.random() * 6;
  const rotation = Math.random() * 360;
  const drift = (Math.random() - 0.5) * 150;

  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{
        left: `${left}%`,
        top: -20,
        width: size,
        height: size * (0.5 + Math.random() * 0.5),
        backgroundColor: color,
        borderRadius: Math.random() > 0.5 ? "50%" : "2px",
      }}
      initial={{ y: -20, x: 0, rotate: 0, opacity: 1 }}
      animate={{
        y: [0, window.innerHeight + 50],
        x: [0, drift],
        rotate: [rotation, rotation + 360 * (Math.random() > 0.5 ? 1 : -1)],
        opacity: [1, 1, 0],
      }}
      transition={{
        duration,
        delay,
        ease: "easeIn",
      }}
    />
  );
}

export default function CelebrationOverlay({
  isVisible,
  xpEarned,
  currentStreak,
  streakIncremented,
  score,
  freezeUsed,
  onDismiss,
}: CelebrationProps) {
  const isCorrect = score >= 1;
  const message = useMemo(
    () => getRandomMessage(isCorrect),
    [isCorrect, isVisible]
  );
  const streakMessage = useMemo(
    () =>
      streakIncremented ? getStreakMilestoneMessage(currentStreak) : null,
    [currentStreak, streakIncremented, isVisible]
  );

  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(onDismiss, 3500);
    return () => clearTimeout(timer);
  }, [isVisible, onDismiss]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center cursor-pointer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onDismiss}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40" />

          {/* Confetti — only on correct answers */}
          {isCorrect && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {Array.from({ length: 40 }).map((_, i) => (
                <ConfettiParticle key={i} index={i} />
              ))}
            </div>
          )}

          {/* Content card */}
          <motion.div
            className="relative z-10 text-center"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 15,
              delay: 0.1,
            }}
          >
            {/* XP earned */}
            <motion.div
              className={`text-5xl font-black mb-3 ${
                isCorrect ? "text-myGreen" : "text-myBlue"
              }`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 250,
                damping: 12,
                delay: 0.2,
              }}
            >
              +{xpEarned} XP
            </motion.div>

            {/* Message */}
            <motion.p
              className="text-white text-xl font-bold mb-2"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {message}
            </motion.p>

            {/* Streak */}
            {streakIncremented && currentStreak > 1 && (
              <motion.p
                className="text-myOrange text-lg font-bold"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                {streakMessage || `${currentStreak} gün seri! 🔥`}
              </motion.p>
            )}

            {/* Freeze used warning */}
            {freezeUsed && (
              <motion.p
                className="text-sky-400 text-sm font-medium mt-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                ❄️ Streak freeze kullanıldı — serin korundu!
              </motion.p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Inline XP popup — pratik cevabı yanında küçük gösterim
export function XPPopup({
  xp,
  isCorrect,
  isVisible,
}: {
  xp: number;
  isCorrect: boolean;
  isVisible: boolean;
}) {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.span
          className={`inline-block font-bold text-sm ml-2 ${
            isCorrect ? "text-myGreen" : "text-myBlue"
          }`}
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 0, y: -30 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        >
          +{xp} XP
        </motion.span>
      )}
    </AnimatePresence>
  );
}
